import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useBrand } from "@/context/BrandContext";

const LINKS = [["#why", "Why fabricators", "why"], ["#how", "How it works", "how"]];

export function HomeHeader() {
  const { brand, logoSrc } = useBrand();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  return (
    <header className="sticky top-0 z-30 backdrop-blur-md bg-[#0b0f17]/80 border-b border-slate-800/60" data-testid="home-header">
      <div className="flex items-center justify-between px-6 sm:px-8 py-4 max-w-[1400px] mx-auto">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="home-logo-link" onClick={close}>
          <img src={logoSrc} alt={brand.product_name} className="w-9 h-9 mix-blend-screen" width="36" height="36" />
          <span className="font-heading font-extrabold text-slate-100">{brand.product_name}</span>
        </Link>
        <nav aria-label="Primary" className="hidden md:flex items-center gap-3 text-sm">
          {LINKS.map(([href, label, id]) => <a key={id} href={href} className="text-slate-400 hover:text-white px-3 py-2" data-testid={`home-nav-${id}`}>{label}</a>)}
          <Link to="/pricing" className="text-slate-300 hover:text-white px-3 py-2" data-testid="home-pricing-link">Pricing</Link>
          <Link to="/login" className="text-slate-300 hover:text-white px-3 py-2" data-testid="home-login-link">Sign in</Link>
          <Link to="/register" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-4 py-2 rounded-md transition-colors" data-testid="home-header-cta">Start 14-day trial</Link>
        </nav>
        <div className="md:hidden flex items-center gap-2">
          <Link to="/register" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-3 py-2 rounded-md text-sm whitespace-nowrap transition-colors" data-testid="home-header-cta-mobile">Start trial</Link>
          <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} aria-controls="home-mobile-menu" aria-label={open ? "Close menu" : "Open menu"} className="p-2 rounded-md border border-slate-700 text-slate-200 hover:border-slate-500" data-testid="home-menu-toggle">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {open && (
        <nav id="home-mobile-menu" aria-label="Mobile" className="md:hidden border-t border-slate-800/60 bg-[#0b0f17]/95 px-6 py-3 flex flex-col text-sm animate-in fade-in slide-in-from-top-1 duration-150" data-testid="home-mobile-menu">
          {LINKS.map(([href, label, id]) => <a key={id} href={href} onClick={close} className="text-slate-300 hover:text-white py-2.5 border-b border-slate-800/60" data-testid={`home-mobile-nav-${id}`}>{label}</a>)}
          <Link to="/pricing" onClick={close} className="text-slate-300 hover:text-white py-2.5 border-b border-slate-800/60" data-testid="home-mobile-pricing-link">Pricing</Link>
          <Link to="/login" onClick={close} className="text-slate-300 hover:text-white py-2.5" data-testid="home-mobile-login-link">Sign in</Link>
        </nav>
      )}
    </header>
  );
}

export function HomeFooter() {
  const { brand } = useBrand();
  return (
    <footer className="border-t border-slate-800/60 mt-8" data-testid="home-footer">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-8 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs text-slate-500">
        <div>© {new Date().getFullYear()} {brand.company_name || brand.product_name}. {brand.product_name} assists experienced estimators, PMs and engineers — it does not replace professional review.</div>
        <div className="flex gap-5">
          <Link to="/pricing" className="hover:text-slate-300" data-testid="home-footer-pricing">Pricing</Link>
          <Link to="/login" className="hover:text-slate-300" data-testid="home-footer-login">Sign in</Link>
          <Link to="/register" className="hover:text-slate-300" data-testid="home-footer-register">Start trial</Link>
          {brand.support_email && <a href={`mailto:${brand.support_email}`} className="hover:text-slate-300" data-testid="home-footer-support">Contact</a>}
        </div>
      </div>
    </footer>
  );
}
